import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, timer} from 'rxjs';
import {switchMap, takeWhile} from 'rxjs/operators';
import {MvsCrudService} from 'features/core/shared/service/crud/mvs-crud.service';
import {CnSimulateRequestDto} from '../../../model/dto/api/cn-simulate-request.dto';
import {CnCompareRequestDto} from '../../../model/dto/api/cn-compare-request.dto';
import {CnSimulationResultDto} from '../../../model/dto/api/cn-simulation-result.dto';
import {CnCompareResultDto} from '../../../model/dto/api/cn-compare-result.dto';
import {CnRunRefDto} from '../../../model/dto/api/cn-run-ref.dto';
import {CnRunStatusDto} from '../../../model/dto/api/cn-run-status.dto';

/** Poll interval in milliseconds (A3: moderate, non-blocking). */
const POLL_INTERVAL_MS = 1750;

/** Terminal states that stop polling. */
const TERMINAL_STATES = ['SUCCESS', 'ERROR'];

/**
 * API service for the cn simulation and comparison REST endpoints.
 *
 * All four backend endpoints are mapped here:
 *   POST /cn/commission-model/{id}/simulate
 *   POST /cn/commission-model/compare
 *   GET  /cn/commission-model/run/{runId}/status
 *   GET  /cn/commission-model/run/{runId}/result
 *
 * simulateAndPoll() and compareAndPoll() handle the full async lifecycle:
 * they start the run, poll status until SUCCESS or ERROR, then fetch and
 * return the final result — all as a single Observable chain.
 */
@Injectable({
    providedIn: 'root'
})
export class CnSimulationApiService {

    private readonly baseUrl = MvsCrudService.baseUrl + '/cn/commission-model';

    constructor(private http: HttpClient) {
    }

    /**
     * POST /cn/commission-model/{id}/simulate
     * Returns either a direct CnSimulationResultDto (small filter) or a CnRunRefDto (async).
     */
    simulate(modelId: number, request: CnSimulateRequestDto): Observable<CnSimulationResultDto | CnRunRefDto> {
        return this.http.post<CnSimulationResultDto | CnRunRefDto>(
            `${this.baseUrl}/${modelId}/simulate`,
            request
        );
    }

    /**
     * POST /cn/commission-model/compare
     * Returns either a direct CnCompareResultDto (small filter) or a CnRunRefDto (async).
     */
    compare(request: CnCompareRequestDto): Observable<CnCompareResultDto | CnRunRefDto> {
        return this.http.post<CnCompareResultDto | CnRunRefDto>(
            `${this.baseUrl}/compare`,
            request
        );
    }

    /**
     * GET /cn/commission-model/run/{runId}/status
     * Returns the current run status including progress counters.
     */
    getRunStatus(runId: number): Observable<CnRunStatusDto> {
        return this.http.get<CnRunStatusDto>(
            `${this.baseUrl}/run/${runId}/status`
        );
    }

    /**
     * GET /cn/commission-model/run/{runId}/result
     * Fetches the final result once the run has reached SUCCESS.
     */
    getRunResult(runId: number): Observable<CnSimulationResultDto | CnCompareResultDto> {
        return this.http.get<CnSimulationResultDto | CnCompareResultDto>(
            `${this.baseUrl}/run/${runId}/result`
        );
    }

    /**
     * Starts a simulate run and polls status until SUCCESS or ERROR,
     * then fetches and emits the final CnSimulationResultDto.
     * Emits each intermediate CnRunStatusDto so the caller can update a progress bar,
     * then completes with the result.
     *
     * If the backend returns the result synchronously (no runId), it is emitted directly.
     */
    simulateAndPoll(
        modelId: number,
        request: CnSimulateRequestDto
    ): Observable<CnRunStatusDto | CnSimulationResultDto> {
        return this.simulate(modelId, request).pipe(
            switchMap((response: CnSimulationResultDto | CnRunRefDto) => {
                if (this.isRunRef(response)) {
                    return this.pollUntilDone(response.runId) as Observable<CnRunStatusDto | CnSimulationResultDto>;
                }
                return new Observable<CnSimulationResultDto>(obs => {
                    obs.next(response as CnSimulationResultDto);
                    obs.complete();
                });
            })
        );
    }

    /**
     * Starts a compare run and polls status until SUCCESS or ERROR,
     * then fetches and emits the final CnCompareResultDto.
     * Emits each intermediate CnRunStatusDto so the caller can update a progress bar,
     * then completes with the result.
     *
     * If the backend returns the result synchronously (no runId), it is emitted directly.
     */
    compareAndPoll(
        request: CnCompareRequestDto
    ): Observable<CnRunStatusDto | CnCompareResultDto> {
        return this.compare(request).pipe(
            switchMap((response: CnCompareResultDto | CnRunRefDto) => {
                if (this.isRunRef(response)) {
                    return this.pollUntilDone(response.runId) as Observable<CnRunStatusDto | CnCompareResultDto>;
                }
                return new Observable<CnCompareResultDto>(obs => {
                    obs.next(response as CnCompareResultDto);
                    obs.complete();
                });
            })
        );
    }

    /**
     * Polls GET /cn/commission-model/run/{runId}/status every POLL_INTERVAL_MS milliseconds,
     * emitting each CnRunStatusDto. Once the status reaches SUCCESS or ERROR,
     * fetches the result and emits it as the final value.
     */
    private pollUntilDone(runId: number): Observable<CnRunStatusDto | CnSimulationResultDto | CnCompareResultDto> {
        return new Observable(subscriber => {
            const subscription = timer(0, POLL_INTERVAL_MS).pipe(
                switchMap(() => this.getRunStatus(runId)),
                takeWhile(
                    (status: CnRunStatusDto) => !TERMINAL_STATES.includes(status.status),
                    true
                )
            ).subscribe({
                next: (status: CnRunStatusDto) => {
                    subscriber.next(status);
                    if (status.status === 'SUCCESS') {
                        this.getRunResult(runId).subscribe({
                            next: result => {
                                subscriber.next(result);
                                subscriber.complete();
                            },
                            error: err => subscriber.error(err)
                        });
                    } else if (status.status === 'ERROR') {
                        subscriber.error(new Error(status.errorMessage || 'Simulation run failed'));
                    }
                },
                error: err => subscriber.error(err)
            });

            return () => subscription.unsubscribe();
        });
    }

    private isRunRef(response: CnSimulationResultDto | CnCompareResultDto | CnRunRefDto): response is CnRunRefDto {
        return (response as CnRunRefDto).runId !== undefined
            && (response as CnSimulationResultDto).yearRows === undefined
            && (response as CnCompareResultDto).comparisonRows === undefined;
    }

}
